import React from 'react';
import { Save, X, ChevronDown, Play, Trash2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';

const DURATION_OPTIONS = [4, 6, 8, 10, 12, 14];

export function TrainingPlanEditor() {
  const { darkMode, theme } = useTheme();
  const { editingPlan, setEditingPlan, savePlan, routines, activatePlan, deletePlan, getActivePlan } = useData();

  if (!editingPlan) return null;

  const isReadOnly = !!editingPlan.readOnly;

  const updateDaysPerWeek = (newCount: number) => {
    if (isReadOnly) return;
    const currentDays = editingPlan.days;
    let newDays;

    if (newCount > currentDays.length) {
      newDays = [
        ...currentDays,
        ...Array.from({ length: newCount - currentDays.length }, (_, i) => ({
          day_number: currentDays.length + i + 1,
          routine_id: '',
        })),
      ];
    } else {
      newDays = currentDays.slice(0, newCount);
    }

    setEditingPlan({
      ...editingPlan,
      days_per_week: newCount,
      days: newDays,
    });
  };

  const updateDayRoutine = (dayIndex: number, routineId: string) => {
    if (isReadOnly) return;
    const newDays = editingPlan.days.map((d, i) =>
      i === dayIndex ? { ...d, routine_id: routineId } : d
    );
    setEditingPlan({ ...editingPlan, days: newDays });
  };

  const updateDayLabel = (dayIndex: number, label: string) => {
    if (isReadOnly) return;
    const newDays = editingPlan.days.map((d, i) =>
      i === dayIndex ? { ...d, label: label.slice(0, 30) } : d
    );
    setEditingPlan({ ...editingPlan, days: newDays });
  };

  // Resolve routine name for read-only display
  const getRoutineName = (routineId: string) => {
    const routine = routines.find(r => r.id === routineId);
    return routine?.name || 'Rutina eliminada';
  };

  return (
    <div
      className="fixed inset-x-0 top-0 z-50 flex h-[100dvh] items-center justify-center px-3 pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-[calc(env(safe-area-inset-bottom)+0.75rem)] sm:p-6"
      role="dialog"
      aria-modal="true"
      onClick={() => setEditingPlan(null)}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div
        className={`animate-rise-in relative w-full max-w-2xl max-h-[calc(100dvh-1.5rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] sm:max-h-[85vh] overflow-y-auto rounded-[30px] border shadow-xl ${
          darkMode
            ? 'border-stone-800 bg-[linear-gradient(180deg,rgba(28,25,21,0.98),rgba(18,16,13,0.98))]'
            : 'border-[#e2c3ad] bg-[linear-gradient(180deg,rgba(255,249,243,0.98),rgba(247,238,228,0.98))]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`p-4 sm:p-6 space-y-4 ${isReadOnly ? 'pb-[calc(env(safe-area-inset-bottom)+5rem)] sm:pb-24' : 'pb-[calc(env(safe-area-inset-bottom)+9rem)] sm:pb-40'}`}>
          {/* Nombre */}
          {isReadOnly ? (
            <h3 className="text-2xl font-black tracking-[-0.04em]">{editingPlan.name}</h3>
          ) : (
            <input
              type="text"
              placeholder="Nombre del plan"
              value={editingPlan.name}
              onChange={(e) =>
                setEditingPlan({ ...editingPlan, name: e.target.value.slice(0, 50) })
              }
              maxLength={50}
              className={`w-full px-4 py-3 sm:py-2 rounded-lg border ${theme.input} text-base sm:text-sm`}
            />
          )}

          {/* Días por semana */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>
              Días por semana
            </label>
            {isReadOnly ? (
              <p className="text-sm">{editingPlan.days_per_week} días</p>
            ) : (
              <div className="flex gap-1.5">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => updateDaysPerWeek(n)}
                    className={`pressable flex-1 py-2 rounded-2xl text-sm font-medium transition-colors touch-manipulation ${
                      editingPlan.days_per_week === n
                        ? 'bg-gradient-to-r from-[#b4532a] to-[#d97745] text-white'
                        : `border ${theme.border} ${theme.hover}`
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Duración */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>
              Duración (semanas)
            </label>
            {isReadOnly ? (
              <p className="text-sm">{editingPlan.duration_weeks} semanas</p>
            ) : (
              <div className="relative">
                <select
                  value={editingPlan.duration_weeks}
                  onChange={(e) =>
                    setEditingPlan({ ...editingPlan, duration_weeks: Number(e.target.value) })
                  }
                  className={`w-full px-4 py-3 sm:py-2 rounded-lg border ${theme.input} text-base sm:text-sm appearance-none pr-10`}
                >
                  {DURATION_OPTIONS.map((w) => (
                    <option key={w} value={w}>
                      {w} semanas
                    </option>
                  ))}
                </select>
                <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${theme.textSecondary}`} />
              </div>
            )}
          </div>

          {/* Días */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>
              {isReadOnly ? 'Rutinas asignadas' : 'Asignar rutinas'}
            </label>
            <div className="space-y-2">
              {editingPlan.days.map((day, index) => (
                <div
                  key={index}
                  className={`rounded-[22px] border ${theme.border} p-3 space-y-2 ${darkMode ? 'bg-[#17130f]' : 'bg-white/55'}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium w-14 shrink-0 ${theme.textSecondary}`}>
                      Día {day.day_number}
                    </span>
                    {isReadOnly ? (
                      <span className="text-sm flex-1">
                        {getRoutineName(day.routine_id)}
                        {day.label && <span className={`ml-2 ${theme.textSecondary}`}>({day.label})</span>}
                      </span>
                    ) : (
                      <div className="relative flex-1">
                        <select
                          value={day.routine_id}
                          onChange={(e) => updateDayRoutine(index, e.target.value)}
                          className={`w-full px-3 py-2 rounded-lg border ${theme.input} text-sm appearance-none pr-8`}
                        >
                          <option value="">Seleccionar rutina...</option>
                          {routines.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className={`absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${theme.textSecondary}`} />
                      </div>
                    )}
                  </div>
                  {!isReadOnly && (
                    <input
                      type="text"
                      placeholder="Etiqueta (opcional)"
                      value={day.label || ''}
                      onChange={(e) => updateDayLabel(index, e.target.value)}
                      maxLength={30}
                      className={`w-full px-3 py-1.5 rounded-lg border ${theme.input} text-xs`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className={`sticky bottom-0 left-0 right-0 z-20 border-t ${theme.border} backdrop-blur-md px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:p-6 ${
            darkMode ? 'bg-[#17130f]/94' : 'bg-[#fbf6ef]/94'
          }`}
        >
          {isReadOnly ? (
            <div className="flex flex-row gap-2">
              {!getActivePlan() && editingPlan.id && (
                <button
                  onClick={() => { setEditingPlan(null); activatePlan(editingPlan.id!); }}
                  className="pressable flex-1 py-3 rounded-[20px] bg-green-600 text-white font-medium hover:bg-green-700 active:bg-green-800 flex items-center justify-center gap-2 touch-manipulation text-sm sm:text-base"
                >
                  <Play className="w-5 h-5" />
                  Reactivar
                </button>
              )}
              {editingPlan.id && (
                <button
                  onClick={() => { setEditingPlan(null); deletePlan(editingPlan.id!); }}
                  className="pressable flex-1 py-3 rounded-[20px] bg-red-600 text-white font-medium hover:bg-red-700 active:bg-red-800 flex items-center justify-center gap-2 touch-manipulation text-sm sm:text-base"
                >
                  <Trash2 className="w-5 h-5" />
                  Eliminar
                </button>
              )}
                <button
                  onClick={() => setEditingPlan(null)}
                className={`pressable flex-1 py-3 rounded-[20px] border ${theme.border} font-medium ${theme.hover} flex items-center justify-center gap-2 touch-manipulation text-sm sm:text-base`}
              >
                <X className="w-5 h-5" />
                Cerrar
              </button>
            </div>
          ) : (
              <div className="flex flex-row gap-2">
                <button
                  onClick={savePlan}
                  className="pressable flex-1 py-3 rounded-[20px] bg-gradient-to-r from-[#b4532a] to-[#d97745] text-white font-medium hover:brightness-105 flex items-center justify-center gap-2 touch-manipulation text-sm sm:text-base"
                >
                  <Save className="w-5 h-5" />
                  Guardar
                </button>
                <button
                  onClick={() => setEditingPlan(null)}
                  className="pressable flex-1 py-3 rounded-[20px] bg-red-600 text-white font-medium hover:bg-red-700 active:bg-red-800 flex items-center justify-center gap-2 touch-manipulation text-sm sm:text-base"
                >
                  <X className="w-5 h-5" />
                  Cancelar
                </button>
              </div>
          )}
        </div>
      </div>
    </div>
  );
}
